/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { Button, Col, Row } from 'reactstrap';
import { ClickHandlers, HomepageProductCard } from '../types';
import { createOnClickFromExternalUrl } from '@utils/click-handler';
import config from '../../../../config';
import './ProductCards.scss';
import classNames from 'classnames';
import { ThemedImage } from '@features/theme/components/ThemedImage';

interface ProductCardProps {
  card: HomepageProductCard;
  mode: 'simple' | 'detailed';
  className?: string;
  classes?: {
    img?: string;
  }
}

interface ProductCardsProps extends Pick<ProductCardProps, 'classes'> {
  cards: HomepageProductCard[];
  showToli?: boolean;
  columns?: 3 | 4;
  clickHandlers?: ClickHandlers;
  mode?: 'simple' | 'detailed';
}

export const ProductCardFooter = ({ imageUrl }: { imageUrl: string | undefined }) => {
  if (!imageUrl) {
    return null;
  }

  return (
    <>
      <div style={{ fontSize: '6px' }} className='mt-3'>
        powered by
      </div>
      <ThemedImage alt='logo-footer' src={imageUrl} width={60} />
    </>
  );
};

const ProductCard = (props: ProductCardProps) => {
  const { card, mode, className = '' } = props;

  if (card.disabled) {
    return (
      <div className={`product-card ${className} product-card-disabled`}>
        <div className='product-card-image'>
          <ThemedImage src={card.logo} alt={'Logo'} />
        </div>
      </div>
    );
  }

  return (
    <div className={`product-card ${className} ${card.onClick ? 'cursor-pointer' : ''}`} onClick={card.onClick} data-testid={card.title}>
      <div className='product-card-fader' style={card.color ? { background: card.color } : undefined} />
      <div className={classNames('product-card-image', props.classes?.img)}>
        <ThemedImage src={card.logo} alt={'Logo'} />
      </div>
      {card.status && card.status === 'pending' ? (
        <div className='pending-tag text-xs text-uppercase background-ThemeAccentExtralight text-ThemeAccentDark'>
          {card.status}
        </div>
      ) : null}
      <div className='product-card-buttons'>
        {mode !== 'simple' ? card.buttons?.map((button, i) => {
          if (button === undefined) return null;
          return (
            <Button
              key={`button-${i}`}
              color={button.color ?? 'transparent'}
              size='xs'
              className='mx-1'
              outline={button.outline ?? true}
              onClick={(e) => {
                e.stopPropagation();
                button.onClick(e);
              }}
            >
              {button.text}
            </Button>
          );
        }) : null}
      </div>
      {card.footer ? <div className='product-card-footer'>{card.footer}</div> : null}
    </div>
  );
}

const ToliCard = () => {
  const handleClick = createOnClickFromExternalUrl(config.toli.url);

  return (
    <div className='product-card cursor-pointer' onClick={handleClick} data-testid='Toli'>
      <div className='product-card-fader' />
      <div className='product-card-image'>
        <ThemedImage src={config.toli.logo} alt='Toli Logo' />
      </div>
      <div className='product-card-buttons'>
        <Button size='xs' color='primary' onClick={handleClick}>
          Log In
        </Button>
      </div>
    </div>
  );
};

export const ProductCards = (props: ProductCardsProps) => {
  const { cards, showToli, columns = 4, mode = 'detailed' } = props;

  const columnClassNames = columns === 3 ? 'col-12 col-sm-6 col-md-4' : 'col-12 col-sm-6 col-md-4 col-lg-3';
  const cardClassName = columns === 3 ? 'product-card-small' : '';
  return (
    <>
      <Row className='product-cards'>
        {cards.map((card, i) => (
          <Col key={`card-${i}`} className={columnClassNames}>
            <ProductCard card={card} mode={mode} className={cardClassName} classes={props.classes} />
          </Col>
        ))}
        {showToli ? (
          <Col className={columnClassNames}>
            <ToliCard />
          </Col>
        ) : null}
      </Row>
    </>
  );
}
